/* eslint-disable no-param-reassign */
import { isEmpty } from "lodash";
import { getParsedValue } from "./form.service";
import { checkEquality, doReduce } from "./form.validation.service";
import { convertNumberToOrdinal } from "../../../utils/helpers";

const getDgList = (components = []) => {
  const keys = [];
  components.forEach((item) => {
    if (item.type === "panel") getDgList(item?.components);
    if (item.type === "datagrid") keys.push(item.key);
  });
  return keys;
};

export const createDatagridMap = (tabItems) =>
  tabItems.reduce(
    (acc, cur, i) => ({ ...acc, [i]: getDgList(cur?.components) }),
    {}
  );

export const hasUnsavedDgRecord = (getDgState, currentIndex, methodRefs) => {
  const { dgConfigs, dgKeysWithTabIndex, ...dgState } = getDgState();
  const currentTabDgKeys = dgKeysWithTabIndex[currentIndex] || [];
  const unsavedDgList = [];
  if (currentTabDgKeys.length) {
    currentTabDgKeys.forEach((dgKey) => {
      const currentDg = dgState[dgKey] || [];
      const hasUnsavedRecord = currentDg.some((record) => {
        const dataGridFormMethods = methodRefs[dgKey + record.id];
        return dataGridFormMethods?.getValues()?.status !== "saved";
      });
      if (hasUnsavedRecord)
        unsavedDgList.push(dgConfigs[dgKey]?.title || dgKey);
    });
  }
  return unsavedDgList;
};

const dgTabDisableCheck = (dgKey, dgKeysWithTabIndex, tabState = {}) => {
  const tabIndex = Object.entries(dgKeysWithTabIndex).find(([, keys]) =>
    keys.includes(dgKey)
  )?.[0];
  if (tabIndex !== undefined) return tabState[tabIndex];
  return false;
};

// ownership validation for non-grouped datagrid
export const dgCustomValidation = (data, condition) => {
  const result = { isValid: true };
  if (condition.type === "reduce") {
    const total = doReduce(data, condition);
    const computedValue = total ? parseFloat(total.toFixed(3)) : 0;
    // console.log("dgCustomVal", data, condition, computedValue, total);
    if (!checkEquality(computedValue, condition)) {
      result.isValid = false;
      result.errMsg = condition.errMsg;
    }
  }
  return result;
};

const getGroupFields = (group, condition) => {
  const fields = [];
  if (!group.field || !condition.field) return fields;
  if (Array.isArray(group.field)) fields.push(...group.field);
  else fields.push(group.field);
  if (Array.isArray(condition.field)) fields.push(...condition.field);
  else fields.push(condition.field);
  return [...new Set(fields)];
};

const verifyOwnershipValidation = (
  dgConfigs,
  dgKey,
  dgState,
  result,
  groupsConfig
) => {
  const customConfig = dgConfigs[dgKey]?.custom;
  const condition = customConfig ? getParsedValue(customConfig, dgKey) : null;

  if (condition && !condition.isGrouped) {
    const ownershipValidation = dgCustomValidation(dgState[dgKey], condition);
    if (!ownershipValidation.isValid) {
      result.isError = true;
      result.errMsg = ownershipValidation.errMsg;
    }
  } else if (condition && condition.isGrouped) {
    const { group: groupName } = condition;
    if (!groupName) return;
    if (!groupsConfig[groupName]) groupsConfig[groupName] = { ...condition };
    const group = groupsConfig[groupName];
    group.field = getGroupFields(group, condition);
    group.dgKeys = [...(group.dgKeys ? group.dgKeys : []), dgKey];
  }
};

const validateGroups = (groupsConfig, dgState, result) => {
  Object.keys(groupsConfig || {}).forEach((group) => {
    if (!result.isError) {
      const { dgKeys, errMsg } = groupsConfig[group];
      let computedValue = 0;
      (dgKeys || []).forEach((key) => {
        computedValue += doReduce(dgState[key], groupsConfig[group]) || 0;
      });
      computedValue = computedValue ? parseFloat(computedValue.toFixed(3)) : 0;
      if (!checkEquality(computedValue, groupsConfig[group])) {
        result.isError = true;
        result.errMsg = errMsg;
      }
    }
  });
};

const isConditionMet = (when, eq, currentValue) =>
  when && eq && currentValue !== undefined && eq.includes(currentValue);

export const getMaxAllowedRecordCount = (
  config,
  maxRecordCount,
  _watchFieldValues
) => {
  const { limit, type, condition = [] } = config || {};
  if (!condition || !condition.length || !type) return maxRecordCount;
  const watchFieldValues = Array.isArray(_watchFieldValues)
    ? _watchFieldValues
    : [];
  let isConditionMatched = false;
  if (type === "or" && watchFieldValues.length)
    isConditionMatched = condition?.some(({ when, eq }, i) =>
      isConditionMet(when, eq, watchFieldValues[i])
    );
  if (type === "and" && watchFieldValues.length)
    isConditionMatched = condition?.every(({ when, eq }, i) =>
      isConditionMet(when, eq, watchFieldValues[i])
    );
  return isConditionMatched ? limit : maxRecordCount;
};

const verifyMaxLimitValidation = (dgConfig, recordsCount, formData, result) => {
  const { title, custom } = dgConfig || {};
  const { maxRecordCount, conditionalLimit } = custom
    ? getParsedValue(custom, "verifyMaxLimitValidation")
    : {};
  const { type, condition = [] } = conditionalLimit || {};
  if (condition?.length && type) {
    const controllingFieldValues = condition.map(({ when }) => formData[when]);
    const maxRecordAllowedCount = getMaxAllowedRecordCount(
      conditionalLimit,
      maxRecordCount,
      controllingFieldValues
    );
    if (recordsCount > maxRecordAllowedCount) {
      const warningConfig = {
        type: "maxLimit",
        errMsg:
          "The number of entries under below mentioned sections have exceeded the allowed limit. Please check the respective limits and remove the excess entries.",
      };
      const filteredErrorList = (result.multiErrMsg || []).filter(
        (item) => item.type === "maxLimit"
      );
      result.isError = true;
      result.isMaxLimit = true;
      const dgTitle = title
        ? `${title} (Max limit - ${maxRecordAllowedCount}, found ${recordsCount})`
        : "";
      if (filteredErrorList.length) {
        result.multiErrMsg = filteredErrorList.map((item) => ({
          ...item,
          dgLabels: [...(item.dgLabels || {}), dgTitle],
        }));
      } else {
        result.multiErrMsg = [{ ...warningConfig, dgLabels: [dgTitle] }];
      }
    }
  }
};

// verifying dg records on submitting the application form
export const verifyDgFields = (
  currentDgState,
  methodRefs,
  tabState,
  requireFieldsInfo,
  dgDocuments = {},
  formData = {}
) => {
  const { dgKeys, dgConfigs, dgKeysWithTabIndex, ...dgState } = currentDgState;
  const result = {
    isError: false,
    errMsg: "",
    multiErrMsg: [
      {
        type: "open",
        errMsg: `Please save entries under the following sections by clicking on "Save Record" button before submitting the application.`,
        dgLabels: [],
      },
      {
        type: "close",
        errMsg: `Please fill the required entries under the following section by clicking on "Edit Record" button.`,
        dgLabels: [],
      },
    ],
  };
  if (dgKeys.length) {
    // groupsConfig will have below props
    // {
    //   groupName: {
    //     dgKeys: ["dg1", "dg2",...],
    //     fields: [dg1.field1, dg1.field2, dg2.field1, dg3.field1,...],
    //     equalTo: 100,
    //     action: "add",
    //     group: "ownership", // this will be used as groupName prop
    //     isGrouped: true,
    //     type: "reduce",
    //     errMsg: "message to display",
    //   },
    // };
    const groupsConfig = {};
    dgKeys.forEach((dgKey) => {
      // skip validation if dg's tab is disabled
      const skipValidation = dgTabDisableCheck(
        dgKey,
        dgKeysWithTabIndex,
        tabState
      );
      if (!skipValidation && dgState[dgKey] && dgState[dgKey].length) {
        dgState[dgKey].forEach((record, recordIndex) => {
          const dataGridFormMethods = methodRefs[dgKey + record.id];
          if (dataGridFormMethods) {
            const datagridValues = dataGridFormMethods.getValues() || {};
            const { id: dgId, docRefId } = datagridValues;
            // if record is in editable mode
            const isRecordOpen = datagridValues.status !== "saved";
            if (isRecordOpen) {
              dataGridFormMethods.trigger();
              result.isError = true;
              const dgLabel = dgConfigs[dgKey]?.title || "";
              if (dgLabel) {
                result.multiErrMsg[0].dgLabels.push(dgLabel);
              }
            } else {
              const dgDisplayedFields =
                (requireFieldsInfo.displayedFields[dgKey] || {})[dgId] || [];
              const allDgFields = requireFieldsInfo.requiredFields[dgKey] || {};
              const dgRequiredFields = Object.keys(allDgFields).filter(
                (key) => allDgFields[key]
              );
              const requiredAndDisplayedFields = dgDisplayedFields.filter(
                (key) => dgRequiredFields.includes(key)
              );

              const hasEmptyField = requiredAndDisplayedFields.find((key) => {
                const hasDocuments =
                  dgDocuments[docRefId] &&
                  dgDocuments[docRefId][key] &&
                  dgDocuments[docRefId][key].length > 0;
                if (hasDocuments) return false;

                return !(datagridValues[key] ?? "").toString();
              });
              if (hasEmptyField) {
                result.isError = true;
                const dgLabel = dgConfigs[dgKey]?.title || "";
                if (dgLabel) {
                  const dgNumber = convertNumberToOrdinal(recordIndex + 1);
                  result.multiErrMsg[1].dgLabels.push(
                    `${dgLabel} (${dgNumber})`
                  );
                }
              }
            }
          }
        });
        if (!result.isError || result.isMaxLimit)
          verifyMaxLimitValidation(
            { ...(dgConfigs[dgKey] || {}), dgKey },
            dgState[dgKey].length,
            formData,
            result
          );
        if (!result.isError && !result.errMsg)
          // ownership validation
          verifyOwnershipValidation(
            dgConfigs,
            dgKey,
            dgState,
            result,
            groupsConfig
          );
      }
    });

    // ownership validation based on dg groups
    if (!result.isError && !result.errMsg && !isEmpty(groupsConfig))
      validateGroups(groupsConfig, dgState, result);
  }
  return result;
};
